import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Save, Trophy, Gift, Copy } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function Settings() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', workspace: user?.workspace || '' });
  const [busy, setBusy] = useState(false);
  const [recapBusy, setRecapBusy] = useState(false);
  const [referrals, setReferrals] = useState(null);
  useEffect(() => { api.get('/referrals/me').then(r => setReferrals(r.data)).catch(() => {}); }, []);
  const copyLink = () => {
    navigator.clipboard.writeText(referrals.link);
    toast.success('Referral link copied.');
  };

  const toggleRecap = async (e) => {
    setRecapBusy(true);
    try {
      const { data } = await api.patch('/me', { winner_recap_opt_out: !e.target.checked });
      setUser(data);
      toast.success(e.target.checked ? 'Weekly recap on.' : 'Weekly recap off.');
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRecapBusy(false); }
  };

  const upd = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch('/me', form);
      setUser(data);
      toast.success('Saved.');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-7 max-w-3xl" data-testid="settings-root">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Settings</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Workspace & profile</h1>
      </div>

      <div className="glass rounded-2xl p-6 space-y-4">
        <div className="font-display font-bold text-lg">Profile</div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Name</label>
          <input value={form.name} onChange={upd('name')} className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm" data-testid="settings-name"/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Workspace name</label>
          <input value={form.workspace} onChange={upd('workspace')} className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm" data-testid="settings-workspace"/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/45 mb-2 block">Email (read-only)</label>
          <input value={user?.email || ''} readOnly className="w-full bg-black/20 border border-white/5 rounded-xl px-4 py-3 text-sm text-white/55"/>
        </div>
        <button onClick={save} disabled={busy} className="btn-primary rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="settings-save">
          <Save className="w-4 h-4"/> {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Gift className="w-4 h-4 text-boosta-cyan"/> Refer & earn</div>
        <p className="text-xs text-white/45 mb-4">Share your link — when someone you refer subscribes to any plan, you earn <span className="text-boosta-yellow font-bold">{referrals?.bonus_zap ?? 500} ZAP</span>.</p>
        {referrals && (
          <>
            <div className="flex gap-2 mb-4">
              <input readOnly value={referrals.link} className="flex-1 bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white/70" data-testid="referral-link"/>
              <button onClick={copyLink} className="btn-primary rounded-xl px-4 inline-flex items-center gap-2 text-sm" data-testid="referral-copy"><Copy className="w-4 h-4"/> Copy</button>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><div className="text-xs uppercase tracking-wider text-white/45 mb-1">Sign-ups</div><div className="font-display font-bold text-xl" data-testid="referral-signups">{referrals.referred_signups}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-white/45 mb-1">Converted</div><div className="font-display font-bold text-xl text-boosta-emerald">{referrals.converted}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-white/45 mb-1">ZAP earned</div><div className="font-display font-bold text-xl text-boosta-yellow" data-testid="referral-earned">{referrals.earned_zap.toLocaleString()}</div></div>
            </div>
          </>
        )}
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Trophy className="w-4 h-4 text-boosta-yellow"/> Email preferences</div>
        <p className="text-xs text-white/45 mb-4">Transactional emails (receipts, invites, password resets) are always sent.</p>
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input type="checkbox" checked={!user?.winner_recap_opt_out} onChange={toggleRecap} disabled={recapBusy}
            className="mt-0.5 w-4 h-4 accent-[#00F0FF]" data-testid="settings-recap-toggle"/>
          <span>
            <span className="text-sm font-medium block">Weekly winners recap</span>
            <span className="text-xs text-white/45">A once-a-week email showing which of your A/B winning creatives drove the most reach and engagement.</span>
          </span>
        </label>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="font-display font-bold text-lg mb-3">Plan & ZAP</div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wider text-white/45 mb-1">Current plan</div>
            <div className="font-display font-bold text-xl capitalize">{user?.plan}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-white/45 mb-1">ZAP balance</div>
            <div className="font-display font-bold text-xl text-boosta-yellow">{(user?.zap_credits ?? 0).toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
